import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaShieldAlt,
  FaCreditCard,
  FaEye,
  FaBell,
  FaSearch,
  FaUserShield,
  FaMobile,
  FaQuestionCircle,
} from 'react-icons/fa';
import SubscriptionPackage from '../components/SubscriptionPackage';
import Faq from '../components/Faq';
// FAQ items shared for Home and Guide

import { useGetSubscription } from '../services/hooks/subscribe/subscribe';
import { useWhaleboneEncode } from '../services/hooks/whalebone/whalebone';
import { useGetConsentLogs, useCreateConsentLogs } from '../services/hooks/consent/consent';
import FloatingMessage from '../components/FloatingMessage';
import CustomerReviews from '../components/CustomerReviews';
import SmartsecureTermsPage from './SmartsecureTermsPage';

const alerts = [
  {
    id: 1,
    image: '/slider-1.png',
  },
  {
    id: 2,
    image: '/slider-1.png',
  },
  {
    id: 3,
    image: '/slider-1.png',
  },
];

const features = [
  {
    icon: <FaShieldAlt className="text-[#3723B5] text-xl" />,
    title: 'Keamanan berlapis',
    description: 'Terhindar dari pencurian online.',
  },
  {
    icon: <FaCreditCard className="text-[#3723B5] text-xl" />,
    title: 'Uang terlindungi',
    description: 'Akun & kartu selalu dijaga.',
  },
  {
    icon: <FaEye className="text-[#3723B5] text-xl" />,
    title: 'Proteksi 24 jam nonstop',
    description: 'Kebocoran langsung terdeteksi.',
  },
  {
    icon: <FaBell className="text-[#3723B5] text-xl" />,
    title: 'Bebas Worry',
    description: 'Dapat notifikasi peringatan sebelum terlambat.',
  },
];

const securityFeatures = [
  {
    icon: <FaUserShield className="text-[#3723B5] text-3xl" />,
    title: 'Identitas Selalu Terjaga',
    description: 'Login kamu dipantau nonstop. Hindari risiko akun terkunci',
  },
  {
    icon: <FaSearch className="text-[#3723B5] text-3xl" />,
    title: 'Cegah Kebocoran Data',
    description:
      'Data bocor sering diperdagangkan. Kami mendeteksinya sebelum disalahgunakan',
  },
  {
    icon: <FaBell className="text-[#3723B5] text-3xl" />,
    title: 'Notifikasi Real-Time',
    description:
      'Satu kebocoran bisa langsung merugikan. Dapatkan notifikasi real-time saat itu juga',
  },
  {
    icon: <FaMobile className="text-[#3723B5] text-3xl" />,
    title: 'Tanpa Install Aplikasi',
    description:
      'Aktif langsung dari jaringan SMARTFREN, tanpa aplikasi tambahan',
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const [isActive, setIsActive] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
  const [isConsentRequired, setIsConsentRequired] = useState(false);

  const { data: subscribeData, isLoading: isSubscribeLoading } =
    useGetSubscription({
      enabled: true,
      onSuccess(data) {
        setIsActive(
          data?.data?.subscription?.status === 'active' ? true : false,
        );
      },
      onError(error) {
        const axiosErr = error as any;
        if (axiosErr.response?.status === 401) {
          navigate('/404', { replace: true });
          return;
        } else {
          setErrorMessage('Terjadi Kesalahan. Silakan coba lagi.');
        }
      },
    });

  const { data: consentLogsData, isLoading: isConsentLoading } = useGetConsentLogs({
    enabled: true,
    onSuccess(data) {
      // Show terms modal if consent logs data is null
      const shouldShowModal = data?.data === null;
      setShowTermsModal(shouldShowModal);
      setIsConsentRequired(shouldShowModal);
    },
    onError(error) {
      console.error('Error fetching consent logs:', error);
      // If there's an error, show the terms modal as a fallback
      setShowTermsModal(true);
      setIsConsentRequired(true);
    },
  });

  const { mutate: encodeWhalebone, isLoading } = useWhaleboneEncode({
    onSuccess: (res, pay) => {
      window.location.href = res.data.url;
    },
  });

  const { mutate: createConsentLogs, isLoading: isCreatingConsent } = useCreateConsentLogs({
    onSuccess: (data) => {
      // After successful consent creation, close the modal and refetch consent logs
      setShowTermsModal(false);
      setIsConsentRequired(false);
    },
    onError: (error) => {
      console.error('Error creating consent logs:', error);
      setErrorMessage('Gagal menyimpan persetujuan. Silakan coba lagi.');
    },
  });

  // Handle scroll position change
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollPosition = container.scrollLeft;
      const slideWidth = container.clientWidth;
      const newSlide = Math.round(scrollPosition / slideWidth);
      setCurrentSlide(newSlide);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle dot navigation
  const scrollTo = (index: number) => {
    const container = containerRef.current;
    if (!container) return;

    const slideWidth = container.clientWidth;
    container.scrollTo({
      left: slideWidth * index,
      behavior: 'smooth',
    });
  };

  // Auto scroll
  useEffect(() => {
    const timer = setInterval(() => {
      const nextSlide = (currentSlide + 1) % alerts.length;
      scrollTo(nextSlide);
    }, 3000);

    return () => clearInterval(timer);
  }, [currentSlide]);

  return (
    <main className="min-h-screen bg-white">
      {errorMessage && (
        <FloatingMessage
          text={errorMessage}
          type="error"
          show={true}
          duration={3000}
        />
      )}

      {/* Terms Modal */}
      {showTermsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
          onClick={() => !isConsentRequired && setShowTermsModal(false)}
        >
          <div
            className="w-full max-w-xl bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto flex flex-col md:rounded-2xl md:mb-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white p-4 flex justify-between items-center border-b">
              <h2 className="text-lg font-bold text-gray-800">
                Syarat & Ketentuan
              </h2>
              {!isConsentRequired && (
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="text-4xl leading-none text-gray-600 hover:text-gray-900 transition-colors"
                >
                  ×
                </button>
              )}
            </div>

            {/* Terms Content */}
            <div className="flex-1 overflow-y-auto">
              <SmartsecureTermsPage isModal={true} />
            </div>

            {/* Footer with Checkbox and Button */}
            <div className="sticky bottom-0 bg-white border-t p-4 space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer py-4">
                <input
                  type="checkbox"
                  checked={hasAgreedToTerms}
                  onChange={(e) => setHasAgreedToTerms(e.target.checked)}
                  className="w-5 h-5 accent-[#3723B5] flex-shrink-0"
                />
                <span className="text-sm text-gray-700">
                  Saya setuju dengan Syarat & Ketentuan Smartsecure
                </span>
              </label>
              <button
                onClick={() => {
                  if (isConsentRequired && hasAgreedToTerms) {
                    // Create consent log with current date
                    createConsentLogs({
                      consent_date: new Date().toISOString(),
                      consent: true,
                    });
                  } else if (!isConsentRequired) {
                    setShowTermsModal(false);
                  }
                }}
                disabled={(isConsentRequired && !hasAgreedToTerms) || isCreatingConsent}
                className={`w-full py-3 rounded-xl font-bold transition-all ${
                  (!isConsentRequired || hasAgreedToTerms) && !isCreatingConsent
                    ? 'bg-gradient-to-r from-[#3723B5] to-[#3723B5] text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isCreatingConsent 
                  ? 'Menyimpan...' 
                  : isConsentRequired 
                    ? 'Setuju & Lanjutkan' 
                    : 'Tutup'}
              </button>
            </div>
          </div>
        </div>
      )}

      <>
        {/* Header */}
        <header className="bg-white p-4 centered flex justify-center mt-6">
          <div>
            <img
              src="/logo-smartsecure.png"
              alt="SmartSecure"
              className="h-12"
            />
            <h6 className="text-xs text-center mt-2">Powered by Whalebone</h6>
          </div>
        </header>

        <section className="p-4">
          <div className="centered text-center">
            <h1 className="text-xl font-bold mb-2">
              Data Pribadimu{' '}
              <span className="text-[#3723B5]">Selalu Terlindungi</span>
            </h1>
            <p className="text-sm text-gray-600">
              Dapatkan notifikasi real time setiap kali data pribadimu
              terdeteksi bocor.
              <br />
              <span className="font-bold text-gray-800">
                Transaksi Bank. Pembayaran Online. Akun Sosial Media. Gmail.
                Semua aman.
              </span>
            </p>
            {isActive ? null : (
              <button
                className="w-full bg-gradient-to-r from-[#3723B5] to-[#3723B5] text-white py-3 rounded-xl font-bold mb-6 mt-4"
                onClick={() => navigate('/subscribe')}
              >
                Lindungi data saya
              </button>
            )}
          </div>
          <img
            src={isActive ? '/hero-done.png' : '/hero-img.png'}
            alt={isActive ? 'Ilustrasi Keamanan - Aktif' : 'Ilustrasi Keamanan'}
            className="w-full rounded-xl"
          />

          {/* CTA to Guide - Outline Style with React Icon */}
          <button
            className="w-full border-2 border-[#3723B5] text-[#3723B5] py-3 rounded-xl font-bold mb-6 mt-4 flex items-center justify-center gap-2 shadow-md bg-white hover:bg-[#3723B5] hover:text-white transition-all outline-none focus:ring-2 focus:ring-[#3723B5] focus:ring-offset-2 group"
            onClick={() => navigate('/guide')}
            aria-label="Lihat Panduan Cara Kerja"
          >
            <span className="flex items-center gap-2">
              <FaQuestionCircle className="transition-colors group-hover:text-white text-[#3723B5]" />
              Panduan Cara Kerja
            </span>
          </button>

          {/* Terms and Conditions Button */}
          <button
            className="w-full border-2 border-gray-300 text-gray-700 py-3 rounded-xl font-bold mb-6 flex items-center justify-center gap-2 shadow-md bg-white hover:bg-gray-100 transition-all outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            onClick={() => setShowTermsModal(true)}
            aria-label="Lihat Syarat dan Ketentuan"
          >
            <span>Lihat Syarat & Ketentuan</span>
          </button>

          {/* Active section */}
          {isActive && (
            <div
              className="py-6 mb-6 rounded-xl"
              style={{
                backgroundImage: 'url(/bg-pink.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center top',
              }}
            >
              <div className="bg-white rounded-xl shadow-sm mx-4 p-4">
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <h2 className="text-md font-bold">Selamat Datang!</h2>
                  </div>
                  <div className="text-right">
                    <div className="text-green-600 font-bold">
                      PERLINDUNGAN AKTIF
                    </div>
                    <div className="text-sm text-gray-600">
                      Masa aktif: {subscribeData?.data?.expired_at} hari lagi
                    </div>
                    {/* <a
                      href="/renew"
                      className="text-sm text-[#3723B5] underline font-bold"
                    >
                      Perpanjang Sekarang
                    </a> */}
                  </div>
                </div>

                <button
                  onClick={() => encodeWhalebone({})}
                  className="w-full mt-4 py-3 bg-gradient-to-r from-[#3723B5] to-[#3723B5] text-white font-bold rounded-xl hover:from-[#3723B5] hover:to-[#3723B5] transition-colors"
                >
                  Buka Dashboard SmartSecure
                </button>
              </div>
            </div>
          )}

          {/* Stats Card */}
          <div className="mb-6 bg-gray-50 p-4 rounded-xl shadow-sm">
            <div className=" flex items-start space-x-4">
              <div className="flex-shrink-0">
                <img
                  src="/icon-folder.png"
                  alt="Icon Folder"
                  className="w-16 h-16 object-contain"
                />
              </div>

              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">Faktanya,</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Indonesia masuk 10 besar negara dengan{' '}
                  <b>kebocoran data terbanyak di dunia</b> (2020–2024). Dari
                  transaksi bank, pembayaran online, media sosial, sampai email—
                  semua aktivitas digital berisiko.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/subscribe')}
              className="text-sm w-full py-3 bg-gradient-to-r from-[#3723B5] to-[#3723B5] text-white font-bold rounded-xl shadow-md text-lg animate-fade-in-up mt-4"
              style={{ animationDelay: '0.7s', animationFillMode: 'both' }}
            >
              Lindungi Data Saya Sekarang
              <h6
                className="text-xs font-light text-white text-center"
                style={{ animationDelay: '0.8s', animationFillMode: 'both' }}
              >
                dengan 1 bulan percobaan gratis dan mulai dari 3.000/bulan
              </h6>
            </button>
          </div>

          {/* Alert Carousel */}
          <div className="relative mb-6">
            <div
              ref={containerRef}
              className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar scroll-smooth px-4"
              style={{ scrollBehavior: 'smooth' }}
            >
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="snap-start shrink-0 pr-4 first:pl-0"
                >
                  <img
                    src={alert.image}
                    alt="Info Keamanan"
                    className="w-[325px] h-[130px] rounded-xl object-cover"
                  />
                </div>
              ))}
            </div>

            {/* Dots indicator */}
            <div className="mt-4 flex justify-center space-x-2">
              {alerts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollTo(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSlide ? 'bg-[#3723B5] w-6' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Check Status */}
          {/* <div className="mb-6" hidden={isActive}>
            <div className="border-2 border-[#E91C5D] rounded-2xl p-4">
              <h3 className="font-bold mb-2">Cari Tau Disini</h3>
              <p className="text-sm text-gray-600 mb-4">
                Siapa tau akun Gmail dan data lainnya pernah bocor
              </p>
              <input
                type="email"
                placeholder="Masukkan alamat email"
                className="w-full px-3 py-3 mb-3 text-gray-500 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3723B5]"
              />
              <button
                onClick={() => navigate('/subscribe')}
                className="w-full bg-gradient-to-r from-[#3723B5] to-[#3723B5] text-white py-3 rounded-xl font-bold flex items-center justify-center space-x-2"
              >
                <FaSearch className="text-lg" />
                <span>Cek Gratis Sekarang</span>
              </button>
            </div>
          </div> */}

          {/* Features Grid */}
          <div className="bg-gray-100 p-6 rounded-xl shadow-sm mb-6">
            <img
              src="/logo-smartsecure.png"
              alt="SmartSecure"
              className="h-12 text-center mx-auto mb-4"
            />
            <h2 className="text-xl mb-4 text-center">
              <span className="text-2xl font-extrabold mb-1 drop-shadow-sm text-center">
                <span className="text-[#3723B5]">SMART</span>SECURE
              </span>{' '}
              Hadir Untukmu
            </h2>
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="mt-1">{feature.icon}</div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <hr className="my-4" hidden={isActive} />
            <div className="text-center" hidden={isActive}>
              <h3 className="font-bold mt-4">
                Aktifkan Langsung di Aplikasi MySmartfren
              </h3>
              <p className="text-sm text-gray-600">
                Simpel, tanpa ribet, bikin hidup Bebas Worry
              </p>
            </div>
            <button
              hidden={isActive}
              onClick={() => navigate('/subscribe')}
              className="w-full mt-6 py-3 bg-gradient-to-r from-[#3723B5] to-[#3723B5] text-white font-semibold rounded-lg"
            >
              Coba Gratis Disini
            </button>
          </div>

          {/* How SmartSecure do that? */}
          <section className="p-4 mb-6">
            <h2 className="text-xl font-bold mb-6">
              Ini Cara{' '}
              <span className="text-2xl font-extrabold mb-1 drop-shadow-sm text-center">
                <span className="text-[#3723B5]">SMART</span>SECURE
              </span>{' '}
              Melindungi Datamu
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-1 gap-4 mb-8">
              {securityFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="border-2 border-[#3723B5] rounded-xl p-4 bg-white shadow-sm"
                >
                  <div className="flex items-start space-x-3">
                    <div className="shrink-0 pt-1">{feature.icon}</div>
                    <div>
                      <h3 className="font-bold mb-2">{feature.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Subscription Package */}
          <SubscriptionPackage isActive={isActive} />

          {/* Customer Reviews - Add this after the security features section */}
          <CustomerReviews />
          <hr className="mb-6" />
          {/* FAQ Section */}
          <Faq />
        </section>
      </>
    </main>
  );
}
